import { useTranslation } from 'react-i18next'

import { CommentTool } from '#/shared/ui/comment-tool'
import { CreatePaymentModal } from '#/shared/ui/create-payment-modal'

import { SHIPMENT_COMMENT_TAB } from '../model/comment-panel-tabs'
import type { ShipmentCommentTab } from '../model/comment-panel-tabs'
import { useShipmentCommentPanel } from '../model/use-shipment-comment-panel'

import { ContractTab } from './contract-tab'
import { EmailTab } from './email-tab'
import { FileTab } from './file-tab'
import { NoteTab } from './note-tab'
import type { PanelState } from './panel-state'
import { PaymentTab } from './payment-tab'
import { PhoneTab } from './phone-tab'
import { TaskTab } from './task-tab'

interface ShipmentCommentPanelProps {
  shipmentId?: string
  hiddenTabs?: ShipmentCommentTab[]
  compact?: boolean
}

interface TabContentProps {
  shipmentId: string | undefined
  tab: PanelState['tab']
  task: PanelState['task']
  note: PanelState['note']
  contract: PanelState['contract']
  phone: PanelState['phone']
  file: PanelState['file']
  email: PanelState['email']
  payment: PanelState['payment']
  compact?: boolean
  onClose?: () => void
}

function TabContent({
  shipmentId,
  tab,
  task,
  note,
  contract,
  phone,
  file,
  email,
  payment,
  compact,
  onClose,
}: TabContentProps) {
  if (tab === SHIPMENT_COMMENT_TAB.note)
    return <NoteTab note={note} compact={compact} shipmentId={shipmentId} />
  if (tab === SHIPMENT_COMMENT_TAB.task)
    return <TaskTab task={task} compact={compact} shipmentId={shipmentId} />
  if (tab === SHIPMENT_COMMENT_TAB.phone)
    return <PhoneTab phone={phone} shipmentId={shipmentId} compact={compact} />
  if (tab === SHIPMENT_COMMENT_TAB.email)
    return <EmailTab email={email} shipmentId={shipmentId} compact={compact} />
  if (tab === SHIPMENT_COMMENT_TAB.file)
    return <FileTab file={file} compact={compact} shipmentId={shipmentId} />
  if (tab === SHIPMENT_COMMENT_TAB.contract)
    return (
      <ContractTab contract={contract} compact={compact} onClose={onClose} />
    )
  if (tab === SHIPMENT_COMMENT_TAB.payment)
    return (
      <PaymentTab
        payment={payment}
        shipmentId={shipmentId}
        compact={compact}
        onClose={onClose}
      />
    )
  return null
}

export function ShipmentCommentPanel({
  shipmentId,
  hiddenTabs,
  compact,
}: ShipmentCommentPanelProps) {
  const { t } = useTranslation()
  const {
    tabs,
    tab,
    setTab,
    task,
    note,
    contract,
    phone,
    file,
    email,
    payment,
  } = useShipmentCommentPanel(shipmentId, { hiddenTabs })

  return (
    <>
      <CommentTool
        tabs={tabs}
        activeTab={tab}
        onActiveTabChange={(value) => setTab(value as typeof tab)}
        restingPlaceholder={t('quoteDetails.commentPlaceholder')}
        activateTabOnPlaceholderClick={SHIPMENT_COMMENT_TAB.note}
        variant={compact ? 'compact' : 'default'}
      >
        <TabContent
          shipmentId={shipmentId}
          tab={tab}
          task={task}
          note={note}
          contract={contract}
          phone={phone}
          file={file}
          email={email}
          payment={payment}
          compact={compact}
          onClose={() => setTab(null)}
        />
      </CommentTool>
      <CreatePaymentModal
        open={payment.modalOpen}
        onOpenChange={payment.onModalOpenChange}
        onSubmit={payment.onSubmit}
        isSubmitting={payment.isSubmitting}
      />
    </>
  )
}
